package fr.arcplex.sooner.webui

import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import fr.arcplex.sooner.coherence.CoherenceNav
import fr.arcplex.sooner.coherence.CoherenceNavItem
import fr.arcplex.sooner.coherence.coherenceNavFrom
import fr.arcplex.network.remoteImageUrl
import fr.arcplex.network.logging.Logger
import fr.arcplex.sooner.ui.theme.TVBackground
import fr.arcplex.sooner.ui.theme.TVLine
import fr.arcplex.sooner.ui.theme.TVTxt
import fr.arcplex.sooner.ui.theme.TVOverlay

/**
 * Skin of the burger menu. The legacy view uses the defaults below, the
 * Coherence view derives the same fields from CoherenceThemeState.
 */data class MenuStyle(
    val background: Color = TVBackground,
    val border: Color = TVLine,
    val scrim: Color = TVOverlay,
    val titleColor: Color = TVTxt,
    val separatorColor: Color = Color(0x4DFFFFFF),
    val itemColor: Color = TVTxt,
    val focusBackground: Color = Color.White,
    val focusColor: Color = Color.Black,
    val focusBorder: Color = Color.White,
    val titleSize: TextUnit = 16.sp,
    val itemSize: TextUnit = 18.sp,
    val itemPaddingX: Dp = 24.dp,
    val itemPaddingY: Dp = 10.dp,
    val itemMinHeight: Dp = 44.dp,
    val cornerRadius: Dp = 0.dp,
    // The drawer is as wide as the widest server item: the menu payload is a
    // stack of finished images, so their size defines the panel.
    val panelPadding: Dp = 8.dp,
    val panelMinWidth: Dp = 160.dp,
    val panelMaxWidth: Dp = 420.dp
)

/**
 * Single entry point used by the pages: picks the skin and does nothing when
 * the menu is closed or the server sent no menu items.
 */
@Composable
fun AppMenu(
    menu: Rail,
    isOpen: Boolean,
    coherence: Boolean,
    isTV: Boolean,
    onDismiss: () -> Unit,
    onItemClicked: (RailItem) -> Unit,
    modifier: Modifier = Modifier
) {
    if (!isOpen) return
    if (coherence) {
        CoherenceMenuView(menu, isOpen, onDismiss, onItemClicked, isTV, modifier)
    } else {
        MenuView(menu, isOpen, onDismiss, onItemClicked, isTV, modifier)
    }
}

/**
 * Burger menu for the legacy server-driven UI. The rendering lives in
 * [MenuPanel]; this wrapper only maps the server rail and its style.
 */
@Composable
fun MenuView(
    menu: Rail,
    isOpen: Boolean,
    onDismiss: () -> Unit,
    onItemClicked: (RailItem) -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    val nav = remember(menu) { coherenceNavFrom(menu) } ?: return
    MenuPanel(
        nav = nav,
        style = MenuStyle(),
        isOpen = isOpen,
        onDismiss = onDismiss,
        onItemClicked = { entry ->
            menu.items.firstOrNull { it.uid == entry.uid }?.let(onItemClicked)
        },
        isTV = isTV,
        modifier = modifier
    )
}

/**
 * Drawer shared by every menu skin: header label, text rows with separators,
 * image items, TV focus handling and backdrop dismissal.
 */
@Composable
fun MenuPanel(
    nav: CoherenceNav,
    style: MenuStyle,
    isOpen: Boolean,
    onDismiss: () -> Unit,
    onItemClicked: (CoherenceNavItem) -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    if (!isOpen || nav.items.isEmpty()) return

    val listState = rememberLazyListState()
    val requesters = remember(nav) { List(nav.items.size) { FocusRequester() } }
    var focusedIndex by remember(nav) { mutableStateOf(-1) }
    val actionableIndices = remember(nav) { nav.items.indices.filter { nav.items[it].hasAction } }
    val firstActionable = actionableIndices.firstOrNull() ?: -1

    // The original opens the menu on its first item (spacers have no action).
    LaunchedEffect(nav, isTV) {
        Logger.d("MENU", "open title='${nav.title}' items=${nav.items.size} isTV=$isTV")
        if (!isTV || firstActionable < 0) return@LaunchedEffect
        withFrameNanos {}
        val result = runCatching { requesters[firstActionable].requestFocus() }
        focusedIndex = firstActionable
        Logger.d("MENU", "initial focus index=$firstActionable ok=${result.isSuccess}")
    }

    LaunchedEffect(focusedIndex) {
        if (focusedIndex >= 0) runCatching { listState.animateScrollToItem(focusedIndex) }
    }

    fun close(reason: String) {
        Logger.d("MENU", "close ($reason)")
        onDismiss()
    }

    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        // Width follows the payload: menu objects are images laid out by the
        // server, so a wider item pushes a wider drawer.
        val widestItem = nav.items.maxOfOrNull { it.width }?.dp ?: 0.dp
        val panelWidth = (widestItem + style.panelPadding * 2)
            .coerceIn(style.panelMinWidth, minOf(maxWidth, style.panelMaxWidth))

        // Backdrop: any click outside the panel closes the menu.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(style.scrim)
                .clickable { close("backdrop") }
        )

        Box(
            modifier = Modifier
                .width(panelWidth)
                .fillMaxHeight()
                .background(style.background)
                .border(1.dp, style.border)
        ) {
            Column(modifier = Modifier.fillMaxHeight()) {
                if (nav.title.isNotBlank()) {
                    Text(
                        text = nav.title,
                        color = style.titleColor,
                        fontSize = style.titleSize,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = style.itemPaddingX, vertical = 16.dp)
                    )
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(1.dp)
                            .background(style.separatorColor)
                    )
                }

                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize().focusGroup()
                ) {
                    itemsIndexed(nav.items, key = { _, entry -> entry.uid }) { index, entry ->
                        val previous = nav.items.getOrNull(index - 1)
                        if (index > 0 && previous?.isImage == false && !entry.isImage) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = style.itemPaddingX)
                                    .height(1.dp)
                                    .background(style.separatorColor)
                            )
                        }
                        val rowModifier = if (entry.hasAction) {
                            val position = actionableIndices.indexOf(index)
                            val next = actionableIndices[
                                (position + 1) % actionableIndices.size
                            ]
                            val previous = actionableIndices[
                                (position - 1 + actionableIndices.size) % actionableIndices.size
                            ]
                            Modifier
                                .focusRequester(requesters[index])
                                .focusProperties {
                                    down = requesters[next]
                                    up = requesters[previous]
                                    // Drawer: horizontal moves stay on the row.
                                    left = requesters[index]
                                    right = requesters[index]
                                }
                                .onFocusChanged { state ->
                                    if (state.isFocused && focusedIndex != index) focusedIndex = index
                                }
                        } else {
                            Modifier
                        }
                        MenuRow(
                            entry = entry,
                            style = style,
                            modifier = rowModifier,
                            onClick = {
                                Logger.d("MENU", "select '${entry.label}' type=${entry.actionType}")
                                // Every selection closes the drawer, including displayMenu.
                                close("select")
                                onItemClicked(entry)
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MenuRow(
    entry: CoherenceNavItem,
    style: MenuStyle,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    var focused by remember { mutableStateOf(false) }
    val actionable = entry.hasAction

    if (entry.isImage) {
        // Server images already contain the label and the spacing (spacers are
        // items without an action), so they are drawn at their exact size.
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = modifier
                    .size(width = entry.width.dp, height = entry.height.dp)
                    .onFocusChanged { focused = it.isFocused }
                    .then(if (focused) Modifier.border(3.dp, style.focusBorder) else Modifier)
                    .then(if (actionable) Modifier.clickable(onClick = onClick) else Modifier)
            ) {
                AsyncImage(
                    model = entry.icon?.let(::remoteImageUrl),
                    contentDescription = entry.label,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxSize()
                )
            }
        }
        return
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = style.itemMinHeight)
            .background(
                color = if (focused && actionable) style.focusBackground else Color.Transparent,
                shape = RoundedCornerShape(style.cornerRadius)
            )
            .onFocusChanged { focused = it.isFocused }
            .then(if (actionable) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = style.itemPaddingX, vertical = style.itemPaddingY)
    ) {
        Text(
            text = entry.label,
            color = when {
                !actionable -> style.itemColor.copy(alpha = 0.6f)
                focused -> style.focusColor
                else -> style.itemColor
            },
            fontSize = style.itemSize,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}
